import { useMemo } from "react";
import { useSetPageTitle } from "src/hooks/useSetPageTitle";
import { AppLayout } from "src/components/layout/app-layout";
import ManageClientsTab from "./tabs/manage-clients.tab";
import ManageAccountsTab from "./tabs/manage-accounts.tab";
import ManageCampaignsTab from "./tabs/manage-campaigns.tab";
import SettingsTab from "./tabs/settings.tab";
import ManageLaunchedCampaignsTab from "./tabs/manage-launched-campaigns.tab";
import ManageUsersTab from "./tabs/manage-users.tab";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useNewCampaignRequestCount } from "src/features/manage-clients/campaigns/hooks/use-new-campaign-request-count";
import "src/features/manage-clients/campaigns/endpoints/launched-campaigns.endpoints";

const CLIENT_MANAGEMENT_TABS = [
  {
    id: '',
    key: 'clients',
    label: 'Clients',
  },
  {
    id: 'manage-accounts',
    key: 'accounts',
    label: 'Accounts',
  },
  {
    id: 'manage-campaigns',
    key: 'campaigns',
    label: 'Campaigns',
  },
  {
    id: 'settings',
    key: 'settings',
    label: 'Settings',
  },
  {
    id: 'users',
    key: 'users',
    label: 'Users',
  },
  {
    id: 'campaigns',
    key: 'launched-campaigns',
    label: 'Requests',
  },
] as const;

const ClientManagementPage = () => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const [searchParams] = useSearchParams();
  const newRequestCount = useNewCampaignRequestCount();

  const clientManagementTabs = useMemo(
    () => CLIENT_MANAGEMENT_TABS.map((clientTab) => {
      if (clientTab.id === 'campaigns' && newRequestCount > 0) {
        return {
          ...clientTab,
          badge: 1,
          badgeVariant: 'dot' as const,
        };
      }
      return clientTab;
    }),
    [newRequestCount],
  );

  const validTabs = clientManagementTabs.map(tab => tab.id);
  type TabType = typeof validTabs[number];

  const currentTab: TabType = validTabs.includes(tab as TabType)
    ? (tab as TabType)
    : '';

  const pageTitle =
    clientManagementTabs.find((t) => t.id === currentTab)?.label ?? 'Clients';
  useSetPageTitle(pageTitle);

  const handleTabChange = (tabId: string) => {
    const qs = searchParams.toString();
    navigate(`/manage-clients/${tabId}${qs ? `?${qs}` : ''}`);
  };

  return (
    <AppLayout
      activePage="/client-management"
      navbarProps={{
        tabs: clientManagementTabs,
        activeTab: currentTab,
        onTabChange: handleTabChange,
      }}
    >

      <div className="h-[calc(100vh-8rem)]">
        {currentTab === '' && <ManageClientsTab />}
        {currentTab === 'manage-accounts' && <ManageAccountsTab />}
        {currentTab === 'manage-campaigns' && <ManageCampaignsTab />}
        {currentTab === 'settings' && <SettingsTab />}
        {currentTab === 'users' && <ManageUsersTab />}
        {currentTab === 'campaigns' && <ManageLaunchedCampaignsTab />}
      </div>

    </AppLayout>
  );
};

export default ClientManagementPage;
